import type { AnimaticMode, TimelineClip, TimelineDocument, TimelineTrack } from "./types"

export type { AnimaticMode } from "./types"

export const SCHEMA_VERSION = 1
export const MAX_VIDEO_TRACKS = 5
/** Минимальная длина клипа, чтобы лезвие/resize не оставляли вырожденные клипы. */
export const MIN_CLIP_MS = 40
/** Минимальное окно correction-клипа (trim для video correction). */
export const CORRECTION_MIN_CLIP_MS = 4_000
/** Пределы скорости клипа (rate-stretch и слайдер). */
export const MIN_SPEED = 0.25
export const MAX_SPEED = 4

/** Source video long enough for correction track (matches server min_trim_seconds). */
export function correctionSourceEligible(durationMs: number | null | undefined): durationMs is number {
  return durationMs != null && durationMs >= CORRECTION_MIN_CLIP_MS
}

/**
 * Округляет позицию до ближайшего кадра и держит её внутри [1, durationMs-1]:
 * ровно 0 и ровно конец не годятся ни для маркера, ни для разреза.
 */
export function snapMsToFrame(ms: number, durationMs: number, frameMs: number): number {
  const hi = Math.max(1, durationMs - 1)
  const step = frameMs > 0 ? frameMs : 40
  const snapped = Math.round(ms / step) * step
  return Math.min(hi, Math.max(1, Math.round(snapped)))
}

export function generateId(): string {
  return Math.random().toString(36).slice(2, 10) + Date.now().toString(36).slice(-4)
}

/** Скорость клипа (отсутствие поля или мусор = 1). */
export function clipSpeed(clip: TimelineClip): number {
  const speed = clip.speed
  return typeof speed === "number" && Number.isFinite(speed) && speed > 0 ? speed : 1
}

/** Громкость дорожки 0..1 (отсутствие поля или мусор = 1). */
export function trackVolume(track: TimelineTrack): number {
  const volume = track.volume
  return typeof volume === "number" && Number.isFinite(volume) ? Math.min(Math.max(volume, 0), 1) : 1
}

/** Эффективный источник клипа video/version_audio: per-clip override или версия дорожки. */
export function clipVersionId(track: TimelineTrack, clip: TimelineClip): number | null {
  return clip.shot_media_version_id ?? track.shot_media_version_id ?? null
}

/** Дорожка ссылается на сегмент аниматика шота, а не на версию / audio_asset. */
export function isAnimaticTrack(track: TimelineTrack): boolean {
  return track.animatic_segment_id != null
}

/**
 * Цель серверного захвата кадра из клипа под плейхедом.
 * null — аниматик (нет ShotMediaVersion) или клип без version id: бэкенд
 * иначе снял бы кадр с legacy-видео шота.
 */
export function clipFrameCaptureTarget(
  track: TimelineTrack,
  clip: TimelineClip,
  timelineMs: number,
): { versionId: number; sourceMs: number } | null {
  if (isAnimaticTrack(track)) return null
  const versionId = clipVersionId(track, clip)
  if (versionId == null) return null
  const sourceMs = clip.source_in_ms + (timelineMs - clip.timeline_start_ms) * clipSpeed(clip)
  return {
    versionId,
    sourceMs: Math.round(Math.min(Math.max(sourceMs, clip.source_in_ms), clip.source_out_ms)),
  }
}

/**
 * Убирает аниматик-дорожки, чей сегмент не связан с шотом (отвязка или замена).
 * linkedSegmentId = null — сегмента нет, все аниматик-дорожки выкидываются.
 */
export function dropStaleAnimaticTracks(doc: TimelineDocument, linkedSegmentId: number | null): TimelineDocument {
  const tracks = doc.tracks.filter(
    (track) => !isAnimaticTrack(track) || (linkedSegmentId != null && track.animatic_segment_id === linkedSegmentId),
  )
  if (tracks.length === doc.tracks.length) return doc
  return normalizeDocument({ ...doc, tracks })
}

/** Длительность клипа на таймлайне: source-диапазон, сжатый/растянутый скоростью. */
export function clipDuration(clip: TimelineClip): number {
  return (clip.source_out_ms - clip.source_in_ms) / clipSpeed(clip)
}

export function clipEnd(clip: TimelineClip): number {
  return clip.timeline_start_ms + clipDuration(clip)
}

export function timelineDuration(doc: TimelineDocument): number {
  let max = 0
  for (const track of doc.tracks) {
    for (const clip of track.clips) max = Math.max(max, clipEnd(clip))
  }
  return max
}

export function emptyDocument(): TimelineDocument {
  return { schema_version: SCHEMA_VERSION, tracks: [] }
}

/**
 * Сортировка клипов и группировка дорожек в каноническом порядке: сначала видео,
 * затем аудио (version_audio и custom_audio вперемешку). Внутри групп порядок
 * документа сохраняется — его задаёт пользователь. Возвращает новый документ.
 */
export function normalizeDocument(doc: TimelineDocument): TimelineDocument {
  const tracks = doc.tracks
    .map((track, index) => ({
      track: {
        ...track,
        clips: [...track.clips].sort((a, b) => a.timeline_start_ms - b.timeline_start_ms),
      },
      key: (track.kind === "video" ? 0 : 1000) + index,
    }))
    .sort((a, b) => a.key - b.key)
    .map(({ track }) => track)
  return { schema_version: SCHEMA_VERSION, tracks }
}

function updateTrack(
  doc: TimelineDocument,
  trackId: string,
  fn: (track: TimelineTrack) => TimelineTrack,
): TimelineDocument {
  return {
    ...doc,
    tracks: doc.tracks.map((track) => (track.id === trackId ? fn(track) : track)),
  }
}

function updateClip(
  doc: TimelineDocument,
  trackId: string,
  clipId: string,
  fn: (clip: TimelineClip) => TimelineClip,
): TimelineDocument {
  return updateTrack(doc, trackId, (track) => ({
    ...track,
    clips: track.clips.map((clip) => (clip.id === clipId ? fn(clip) : clip)),
  }))
}

export function findTrack(doc: TimelineDocument, trackId: string): TimelineTrack | undefined {
  return doc.tracks.find((t) => t.id === trackId)
}

export function findClip(doc: TimelineDocument, trackId: string, clipId: string): TimelineClip | undefined {
  return findTrack(doc, trackId)?.clips.find((c) => c.id === clipId)
}

// --- Лезвие ---

function splitClip(clip: TimelineClip, atMs: number): [TimelineClip, TimelineClip] | null {
  const offset = atMs - clip.timeline_start_ms
  if (offset < MIN_CLIP_MS || clipDuration(clip) - offset < MIN_CLIP_MS) return null
  // Смещение в timeline-мс переводим в source-мс с учётом скорости клипа.
  const sourceOffset = Math.round(offset * clipSpeed(clip))
  const left: TimelineClip = { ...clip, source_out_ms: clip.source_in_ms + sourceOffset, fade_out_ms: 0 }
  const right: TimelineClip = {
    ...clip,
    id: generateId(),
    source_in_ms: clip.source_in_ms + sourceOffset,
    timeline_start_ms: atMs,
    fade_in_ms: 0,
  }
  if (clip.fade_in_ms === undefined) {
    delete left.fade_out_ms
    delete right.fade_in_ms
  }
  return [left, right]
}

/**
 * Разрезает клипы под плейхедом. Если задан selected — только его,
 * иначе все клипы всех дорожек, накрывающие позицию. Неотделённое аудио
 * (audio_detached=false) режется вместе с видео автоматически: у таких
 * видеодорожек нет отдельной аудиодорожки.
 */
export function bladeAt(
  doc: TimelineDocument,
  atMs: number,
  selected?: { trackId: string; clipId: string },
  lockedClipIds?: ReadonlySet<string>,
): TimelineDocument {
  const tracks = doc.tracks.map((track) => {
    if (selected && track.id !== selected.trackId) return track
    const clips: TimelineClip[] = []
    for (const clip of track.clips) {
      const covers = atMs > clip.timeline_start_ms && atMs < clipEnd(clip)
      const matches = (!selected || clip.id === selected.clipId) && lockedClipIds?.has(clip.id) !== true
      if (covers && matches) {
        const parts = splitClip(clip, atMs)
        if (parts) {
          clips.push(parts[0], parts[1])
          continue
        }
      }
      clips.push(clip)
    }
    return { ...track, clips }
  })
  return { ...doc, tracks }
}

// --- Удаление ---

export function removeClip(doc: TimelineDocument, trackId: string, clipId: string): TimelineDocument {
  return updateTrack(doc, trackId, (track) => ({
    ...track,
    clips: track.clips.filter((c) => c.id !== clipId),
  }))
}

// --- Перемещение ---

/** Кламп новой позиции клипа соседями по дорожке (без наложений). */
export function moveClip(doc: TimelineDocument, trackId: string, clipId: string, newStartMs: number): TimelineDocument {
  const track = findTrack(doc, trackId)
  const clip = track?.clips.find((c) => c.id === clipId)
  if (!track || !clip) return doc

  // Двигаем в пределах текущего зазора между соседями (порядок клипов не меняется).
  const duration = clipDuration(clip)
  const others = track.clips.filter((c) => c.id !== clipId).sort((a, b) => a.timeline_start_ms - b.timeline_start_ms)
  const prev = others.filter((c) => clipEnd(c) <= clip.timeline_start_ms).pop()
  const next = others.find((c) => c.timeline_start_ms >= clipEnd(clip))
  const minStart = prev ? clipEnd(prev) : 0
  const maxStart = next ? next.timeline_start_ms - duration : Infinity
  const start = Math.round(Math.min(Math.max(newStartMs, minStart), Math.max(maxStart, minStart)))
  return updateClip(doc, trackId, clipId, (c) => ({ ...c, timeline_start_ms: start }))
}

// --- Прилипание (snapping) ---

/**
 * Собирает точки прилипания: ноль таймлайна, края всех клипов
 * (кроме перетаскиваемого) и позиция плейхеда.
 */
export function collectSnapPoints(
  doc: TimelineDocument,
  excludeTrackId: string,
  excludeClipId: string,
  playheadMs?: number,
): number[] {
  const points = new Set<number>([0])
  if (playheadMs != null) points.add(Math.round(playheadMs))
  for (const track of doc.tracks) {
    for (const clip of track.clips) {
      if (track.id === excludeTrackId && clip.id === excludeClipId) continue
      points.add(clip.timeline_start_ms)
      points.add(clipEnd(clip))
    }
  }
  return [...points].sort((a, b) => a - b)
}

function nearestSnapPoint(ms: number, points: number[], toleranceMs: number): number | null {
  let best: number | null = null
  let bestDist = toleranceMs
  for (const p of points) {
    const dist = Math.abs(p - ms)
    if (dist <= bestDist) {
      best = p
      bestDist = dist
    }
  }
  return best
}

/** Снап одного края (для resize): ближайшая точка в пределах допуска или исходное значение. */
export function snapEdge(edgeMs: number, points: number[], toleranceMs: number): number {
  return nearestSnapPoint(edgeMs, points, toleranceMs) ?? edgeMs
}

/**
 * Снап при перемещении: проверяет оба края клипа и возвращает скорректированный
 * timeline_start_ms по ближайшей из подошедших точек.
 */
export function snapMoveStart(startMs: number, durationMs: number, points: number[], toleranceMs: number): number {
  const endMs = startMs + durationMs
  let bestStart = startMs
  let bestDist = toleranceMs
  for (const p of points) {
    const startDist = Math.abs(p - startMs)
    if (startDist <= bestDist) {
      bestStart = p
      bestDist = startDist
    }
    const endDist = Math.abs(p - endMs)
    if (endDist <= bestDist) {
      bestStart = p - durationMs
      bestDist = endDist
    }
  }
  return bestStart
}

// --- Восстановление краёв (resize) ---

/**
 * Тянет край клипа: left двигает source_in и timeline_start вместе (восстановление
 * обрезанного начала), right двигает source_out. Клампится длиной источника и соседями.
 */
export function resizeClip(
  doc: TimelineDocument,
  trackId: string,
  clipId: string,
  edge: "left" | "right",
  newEdgeMs: number,
  sourceDurationMs: number | null,
): TimelineDocument {
  const track = findTrack(doc, trackId)
  const clip = track?.clips.find((c) => c.id === clipId)
  if (!track || !clip) return doc

  const prev = track.clips.filter((c) => c.id !== clipId && clipEnd(c) <= clip.timeline_start_ms).pop()
  const next = track.clips.find((c) => c.id !== clipId && c.timeline_start_ms >= clipEnd(clip))

  const speed = clipSpeed(clip)

  if (edge === "left") {
    const minStart = Math.max(
      prev ? clipEnd(prev) : 0,
      clip.timeline_start_ms - clip.source_in_ms / speed, // нельзя уйти раньше начала источника
    )
    const maxStart = clipEnd(clip) - MIN_CLIP_MS
    const start = Math.round(Math.min(Math.max(newEdgeMs, minStart), maxStart))
    const delta = start - clip.timeline_start_ms
    return updateClip(doc, trackId, clipId, (c) => ({
      ...c,
      timeline_start_ms: start,
      source_in_ms: Math.round(c.source_in_ms + delta * speed),
    }))
  }

  const sourceMax = sourceDurationMs ?? Infinity
  const maxEnd = Math.min(
    next ? next.timeline_start_ms : Infinity,
    clip.timeline_start_ms + (sourceMax - clip.source_in_ms) / speed, // не дальше конца источника
  )
  const minEnd = clip.timeline_start_ms + MIN_CLIP_MS
  const end = Math.round(Math.min(Math.max(newEdgeMs, minEnd), maxEnd))
  return updateClip(doc, trackId, clipId, (c) => ({
    ...c,
    source_out_ms: Math.round(c.source_in_ms + (end - c.timeline_start_ms) * speed),
  }))
}

/**
 * После resize correction-клипа: держит source-окно в [minWindowMs, maxWindowMs?].
 * Якорь — противоположный край относительно `edge` (как max-clamp в timeline-editor).
 */
export function clampCorrectionSourceWindow(
  doc: TimelineDocument,
  trackId: string,
  clipId: string,
  edge: "left" | "right",
  minWindowMs: number,
  maxWindowMs?: number | null,
): TimelineDocument {
  const found = findClip(doc, trackId, clipId)
  if (!found) return doc

  const windowMs = found.source_out_ms - found.source_in_ms
  let targetMs = windowMs
  if (typeof maxWindowMs === "number" && maxWindowMs > 0 && targetMs > maxWindowMs) {
    targetMs = maxWindowMs
  }
  if (minWindowMs > 0 && targetMs < minWindowMs) {
    targetMs = minWindowMs
  }
  if (targetMs === windowMs) return doc

  return {
    ...doc,
    tracks: doc.tracks.map((tr) => {
      if (tr.id !== trackId) return tr
      return {
        ...tr,
        clips: tr.clips.map((c) => {
          if (c.id !== clipId) return c
          if (edge === "right") {
            return { ...c, source_out_ms: c.source_in_ms + targetMs }
          }
          const out = c.source_out_ms
          const inMs = Math.max(0, out - targetMs)
          const delta = c.source_in_ms - inMs
          return {
            ...c,
            source_in_ms: inMs,
            source_out_ms: inMs + targetMs,
            timeline_start_ms: Math.max(0, c.timeline_start_ms - delta / clipSpeed(c)),
          }
        }),
      }
    }),
  }
}

// --- Скорость (speed / rate-stretch) ---

function clampSpeed(speed: number): number {
  if (!Number.isFinite(speed) || speed <= 0) return 1
  return Math.min(Math.max(speed, MIN_SPEED), MAX_SPEED)
}

/** Кладёт speed в клип: 1 хранится как отсутствие поля (обратная совместимость). */
function withSpeed(clip: TimelineClip, speed: number): TimelineClip {
  const rounded = Math.round(speed * 100) / 100
  const next = { ...clip }
  if (rounded === 1) delete next.speed
  else next.speed = rounded
  return next
}

/**
 * Меняет скорость клипа с якорем в timeline_start_ms: конец пересчитывается.
 * При замедлении конец не может наехать на следующий клип — speed клампится.
 */
export function setClipSpeed(doc: TimelineDocument, trackId: string, clipId: string, speed: number): TimelineDocument {
  const track = findTrack(doc, trackId)
  const clip = track?.clips.find((c) => c.id === clipId)
  if (!track || !clip) return doc

  let next = clampSpeed(speed)
  const sourceLen = clip.source_out_ms - clip.source_in_ms
  const nextClip = track.clips.find((c) => c.id !== clipId && c.timeline_start_ms >= clipEnd(clip))
  if (nextClip) {
    const maxDuration = nextClip.timeline_start_ms - clip.timeline_start_ms
    if (maxDuration > 0 && sourceLen / next > maxDuration) next = clampSpeed(sourceLen / maxDuration)
  }
  return updateClip(doc, trackId, clipId, (c) => withSpeed(c, next))
}

/**
 * Rate-stretch (Alt+drag края): source-диапазон не меняется, меняется скорость.
 * Правый край: якорь — начало клипа; левый край: якорь — конец (start сдвигается).
 * Клампится MIN/MAX_SPEED, MIN_CLIP_MS и соседями по дорожке.
 */
export function rateStretchClip(
  doc: TimelineDocument,
  trackId: string,
  clipId: string,
  edge: "left" | "right",
  newEdgeMs: number,
): TimelineDocument {
  const track = findTrack(doc, trackId)
  const clip = track?.clips.find((c) => c.id === clipId)
  if (!track || !clip) return doc

  const sourceLen = clip.source_out_ms - clip.source_in_ms
  if (sourceLen <= 0) return doc
  const prev = track.clips.filter((c) => c.id !== clipId && clipEnd(c) <= clip.timeline_start_ms).pop()
  const next = track.clips.find((c) => c.id !== clipId && c.timeline_start_ms >= clipEnd(clip))
  const minDuration = Math.max(MIN_CLIP_MS, sourceLen / MAX_SPEED)
  const maxDuration = sourceLen / MIN_SPEED

  if (edge === "right") {
    const maxEnd = Math.min(next ? next.timeline_start_ms : Infinity, clip.timeline_start_ms + maxDuration)
    const minEnd = clip.timeline_start_ms + minDuration
    const end = Math.min(Math.max(newEdgeMs, minEnd), maxEnd)
    const duration = end - clip.timeline_start_ms
    if (duration <= 0) return doc
    return updateClip(doc, trackId, clipId, (c) => withSpeed(c, sourceLen / duration))
  }

  const endMs = clipEnd(clip)
  const minStart = Math.max(prev ? clipEnd(prev) : 0, endMs - maxDuration)
  const maxStart = endMs - minDuration
  const start = Math.min(Math.max(newEdgeMs, minStart), maxStart)
  const duration = endMs - start
  if (duration <= 0) return doc
  return updateClip(doc, trackId, clipId, (c) => {
    const stretched = withSpeed(c, sourceLen / duration)
    // Фиксируем конец: start пересчитываем от фактической (округлённой) скорости.
    return { ...stretched, timeline_start_ms: Math.round(endMs - clipDuration(stretched)) }
  })
}

// --- Дорожки ---

export function videoTrackCount(doc: TimelineDocument): number {
  return doc.tracks.filter((t) => t.kind === "video").length
}

/** Новая видеодорожка сверху (наивысший приоритет) с полным клипом версии. */
export function addVideoTrack(doc: TimelineDocument, versionId: number, durationMs: number): TimelineDocument {
  if (videoTrackCount(doc) >= MAX_VIDEO_TRACKS) return doc
  const track: TimelineTrack = {
    id: generateId(),
    kind: "video",
    muted: false,
    audio_detached: false,
    shot_media_version_id: versionId,
    clips: [
      {
        id: generateId(),
        source_in_ms: 0,
        source_out_ms: Math.max(durationMs, MIN_CLIP_MS),
        timeline_start_ms: 0,
      },
    ],
  }
  return normalizeDocument({ ...doc, tracks: [track, ...doc.tracks] })
}

/** Draft-дорожка видеокоррекции: полный клип активной версии + purpose=correction. */
export function addCorrectionTrack(doc: TimelineDocument, versionId: number, durationMs: number): TimelineDocument {
  if (videoTrackCount(doc) >= MAX_VIDEO_TRACKS) return doc
  const track: TimelineTrack = {
    id: generateId(),
    kind: "video",
    muted: false,
    audio_detached: false,
    purpose: "correction",
    shot_media_version_id: versionId,
    clips: [
      {
        id: generateId(),
        source_in_ms: 0,
        source_out_ms: Math.max(durationMs, MIN_CLIP_MS),
        timeline_start_ms: 0,
      },
    ],
  }
  return normalizeDocument({ ...doc, tracks: [track, ...doc.tracks] })
}

export function addCustomAudioTrack(doc: TimelineDocument): TimelineDocument {
  const track: TimelineTrack = { id: generateId(), kind: "custom_audio", muted: false, clips: [] }
  return normalizeDocument({ ...doc, tracks: [...doc.tracks, track] })
}

/** custom_audio с одним клипом готового ассета шота, с нуля таймлайна. */
export function addCustomAudioTrackWithAsset(
  doc: TimelineDocument,
  assetId: number,
  durationMs: number | null,
): TimelineDocument {
  const withTrack = addCustomAudioTrack(doc)
  const created = withTrack.tracks.find((track) => !doc.tracks.some((prev) => prev.id === track.id))
  if (!created) return withTrack
  const duration = durationMs != null && durationMs > 0 ? durationMs : FALLBACK_CLIP_MS
  return addAudioAssetClip(withTrack, created.id, assetId, 0, duration)
}

/** Дорожка из связанного сегмента аниматика: видео со звуком, немое видео или только звук. */
export function addAnimaticTrack(
  doc: TimelineDocument,
  params: { segmentId: number; mode: AnimaticMode; startMs: number; endMs: number },
): TimelineDocument {
  const isVideo = params.mode !== "audio_only"
  if (isVideo && videoTrackCount(doc) >= MAX_VIDEO_TRACKS) return doc

  const startMs = Math.max(0, Math.round(params.startMs))
  const endMs = Math.max(startMs + MIN_CLIP_MS, Math.round(params.endMs))
  const clip: TimelineClip = {
    id: generateId(),
    source_in_ms: startMs,
    source_out_ms: endMs,
    timeline_start_ms: 0,
    ...(isVideo ? {} : { fade_in_ms: 0, fade_out_ms: 0 }),
  }

  const track: TimelineTrack = isVideo
    ? {
        id: generateId(),
        kind: "video",
        muted: false,
        audio_detached: false,
        hidden: false,
        animatic_segment_id: params.segmentId,
        animatic_mode: params.mode,
        clips: [clip],
      }
    : {
        id: generateId(),
        kind: "custom_audio",
        muted: false,
        animatic_segment_id: params.segmentId,
        animatic_mode: "audio_only",
        clips: [clip],
      }

  return normalizeDocument({
    ...doc,
    tracks: isVideo ? [track, ...doc.tracks] : [...doc.tracks, track],
  })
}

/**
 * Удаляет дорожку. Для видеодорожки удаляется и её version_audio-дорожка;
 * удаление version_audio физически возвращает аудио в видео (audio_detached=false),
 * но замьюченным (muted=true) и с громкостью удалённой дорожки: пользователь удалил
 * звук — включить его обратно можно одним кликом.
 * Пара матчится по detached_from_track_id; для старых документов без связки —
 * fallback по shot_media_version_id.
 */
export function removeTrack(doc: TimelineDocument, trackId: string): TimelineDocument {
  const track = findTrack(doc, trackId)
  if (!track) return doc
  const isDetachedPairOfVideo = (t: TimelineTrack) =>
    t.kind === "version_audio" &&
    (t.detached_from_track_id != null
      ? t.detached_from_track_id === track.id
      : // Fallback только для реально отделённого видео: удаление недетаченного
        // дубля версии не должно сносить чужую legacy-аудиодорожку.
        track.audio_detached === true && t.shot_media_version_id === track.shot_media_version_id)
  let tracks = doc.tracks.filter((t) => {
    if (t.id === trackId) return false
    if (track.kind === "video" && isDetachedPairOfVideo(t)) return false
    return true
  })
  if (track.kind === "version_audio") {
    // Оставшиеся аудиодорожки блокируют ре-аттач: живая связанная пара — всегда,
    // legacy-дорожка той же версии — как неоднозначная (не знаем, чья она).
    const remaining = tracks.filter((t): t is TimelineTrack & { kind: "version_audio" } => t.kind === "version_audio")
    const linkedTargets = new Set(remaining.map((t) => t.detached_from_track_id).filter((id) => id != null))
    const legacyVersions = new Set(
      remaining.filter((t) => t.detached_from_track_id == null).map((t) => t.shot_media_version_id),
    )
    const isPairVideo = (t: TimelineTrack) =>
      t.kind === "video" &&
      t.audio_detached === true &&
      !linkedTargets.has(t.id) &&
      (track.detached_from_track_id != null
        ? t.id === track.detached_from_track_id
        : t.shot_media_version_id === track.shot_media_version_id && !legacyVersions.has(t.shot_media_version_id))
    tracks = tracks.map((t) => {
      if (!isPairVideo(t)) return t
      const next: TimelineTrack = { ...t, audio_detached: false, muted: true }
      if (track.volume != null) next.volume = track.volume
      else delete next.volume
      return next
    })
  }
  return normalizeDocument({ ...doc, tracks })
}

/**
 * Зеркало серверного инварианта Normalize: detached-видео без парной version_audio
 * возвращает звук замьюченным. Нужен на загрузке документа — сервер чинит только
 * при сохранении, а уже «застрявший» документ иначе открывается без пути восстановления
 * из UI (кнопки звука скрыты, detach заблокирован).
 */
export function repairDetachedAudio(doc: TimelineDocument): TimelineDocument {
  let changed = false
  const tracks = doc.tracks.map((t) => {
    if (t.kind !== "video" || t.audio_detached !== true) return t
    const paired = doc.tracks.some(
      (a) =>
        a.kind === "version_audio" &&
        (a.detached_from_track_id != null
          ? a.detached_from_track_id === t.id
          : a.shot_media_version_id === t.shot_media_version_id),
    )
    if (paired) return t
    changed = true
    return { ...t, audio_detached: false, muted: true }
  })
  return changed ? { ...doc, tracks } : doc
}

export function setTrackMuted(doc: TimelineDocument, trackId: string, muted: boolean): TimelineDocument {
  return updateTrack(doc, trackId, (track) => ({ ...track, muted }))
}

/** Громкость дорожки: 1 хранится как отсутствие поля (обратная совместимость). */
export function setTrackVolume(doc: TimelineDocument, trackId: string, volume: number): TimelineDocument {
  if (!Number.isFinite(volume)) return doc
  const rounded = Math.round(Math.min(Math.max(volume, 0), 1) * 100) / 100
  return updateTrack(doc, trackId, (track) => {
    const next = { ...track }
    if (rounded === 1) delete next.volume
    else next.volume = rounded
    return next
  })
}

/** «Глаз» видеодорожки: hidden-слой исключается из стека видео и рендера. */
export function setTrackHidden(doc: TimelineDocument, trackId: string, hidden: boolean): TimelineDocument {
  return updateTrack(doc, trackId, (track) => ({ ...track, hidden }))
}

/**
 * Диапазон индексов группы дорожки (видео или аудио) в документе.
 * Группы непрерывны благодаря normalizeDocument: сначала видео, затем аудио.
 */
export function trackGroupRange(doc: TimelineDocument, trackId: string): { min: number; max: number } | null {
  const index = doc.tracks.findIndex((t) => t.id === trackId)
  if (index < 0) return null
  const isVideo = doc.tracks[index].kind === "video"
  let min = index
  let max = index
  while (min > 0 && (doc.tracks[min - 1].kind === "video") === isVideo) min--
  while (max < doc.tracks.length - 1 && (doc.tracks[max + 1].kind === "video") === isVideo) max++
  return { min, max }
}

/**
 * Переносит дорожку на targetIndex, клампя его границами её группы (видео с видео,
 * аудио с аудио). Так аудио не поднимается выше нижней видеодорожки,
 * а видео не опускается ниже верхней аудиодорожки.
 */
export function moveTrackToIndex(doc: TimelineDocument, trackId: string, targetIndex: number): TimelineDocument {
  const fromIndex = doc.tracks.findIndex((t) => t.id === trackId)
  const range = trackGroupRange(doc, trackId)
  if (fromIndex < 0 || !range) return doc
  const toIndex = Math.min(Math.max(targetIndex, range.min), range.max)
  if (toIndex === fromIndex) return doc

  const tracks = [...doc.tracks]
  const [track] = tracks.splice(fromIndex, 1)
  tracks.splice(toIndex, 0, track)
  return { ...doc, tracks }
}

/** Отделяет аудио видеодорожки в отдельную version_audio-дорожку с копией клипов. */
export function detachAudio(doc: TimelineDocument, videoTrackId: string): TimelineDocument {
  const video = findTrack(doc, videoTrackId)
  if (!video || video.kind !== "video" || video.audio_detached || isAnimaticTrack(video)) return doc

  const audioTrack: TimelineTrack = {
    id: generateId(),
    kind: "version_audio",
    muted: video.muted,
    ...(video.volume != null ? { volume: video.volume } : {}),
    shot_media_version_id: video.shot_media_version_id,
    detached_from_track_id: video.id,
    clips: video.clips.map((clip) => ({ ...clip, id: generateId(), fade_in_ms: 0, fade_out_ms: 0 })),
  }
  const withFlag = updateTrack(doc, videoTrackId, (track) => ({ ...track, audio_detached: true }))
  return normalizeDocument({ ...withFlag, tracks: [...withFlag.tracks, audioTrack] })
}

// --- Аудио-клипы и фейды ---

export function setClipFade(
  doc: TimelineDocument,
  trackId: string,
  clipId: string,
  edge: "in" | "out",
  fadeMs: number,
): TimelineDocument {
  return updateClip(doc, trackId, clipId, (clip) => {
    const duration = clipDuration(clip)
    if (edge === "in") {
      const fadeIn = Math.round(Math.min(Math.max(fadeMs, 0), duration - (clip.fade_out_ms ?? 0)))
      return { ...clip, fade_in_ms: fadeIn }
    }
    const fadeOut = Math.round(Math.min(Math.max(fadeMs, 0), duration - (clip.fade_in_ms ?? 0)))
    return { ...clip, fade_out_ms: fadeOut }
  })
}

/** Добавляет клип аудио-ассета на кастомную дорожку с позиции startMs (без наложений — сдвигается вправо). */
export function addAudioAssetClip(
  doc: TimelineDocument,
  trackId: string,
  assetId: number,
  startMs: number,
  durationMs: number,
): TimelineDocument {
  const track = findTrack(doc, trackId)
  if (!track || track.kind !== "custom_audio") return doc

  const duration = Math.max(durationMs, MIN_CLIP_MS)
  let start = Math.max(startMs, 0)
  for (const other of [...track.clips].sort((a, b) => a.timeline_start_ms - b.timeline_start_ms)) {
    const overlaps = start < clipEnd(other) && start + duration > other.timeline_start_ms
    if (overlaps) start = clipEnd(other)
  }

  const clip: TimelineClip = {
    id: generateId(),
    audio_asset_id: assetId,
    source_in_ms: 0,
    source_out_ms: duration,
    timeline_start_ms: Math.round(start),
    fade_in_ms: 0,
    fade_out_ms: 0,
  }
  return updateTrack(doc, trackId, (t) => ({
    ...t,
    clips: [...t.clips, clip].sort((a, b) => a.timeline_start_ms - b.timeline_start_ms),
  }))
}

/** Чистит клипы, ссылающиеся на удалённый аудио-ассет. */
export function removeAssetClips(doc: TimelineDocument, assetId: number): TimelineDocument {
  return {
    ...doc,
    tracks: doc.tracks.map((track) => ({
      ...track,
      clips: track.clips.filter((clip) => clip.audio_asset_id !== assetId),
    })),
  }
}

// --- Однодорожечный документ ---

const FALLBACK_CLIP_MS = 5_000

/**
 * Одна video-дорожка, склеенная встык из нескольких источников — лента аниматика.
 *
 * Каждый файл становится отдельным клипом со своим `shot_media_version_id`:
 * движок держит пул медиа-элементов по эффективному источнику клипа и сам
 * переключает видимость на стыке, поэтому непрерывное воспроизведение
 * нескольких файлов не требует от него никаких доработок.
 *
 * Источники без длительности (файл ещё кодируется) пропускаются: клип нулевой
 * длины сдвинул бы всю ленту и увёл сегменты от своих кадров.
 * `sourceInMs`/`sourceOutMs` кладут на ленту окно внутри файла (аниматик).
 * Клип без URL в versionUrls остаётся в документе: время идёт, элемента нет.
 */
export function buildConcatenatedVideoDocument(
  sources: Array<{
    sourceId: number
    durationMs: number | null | undefined
    /** Окно внутри файла. Без него клип играет durationMs с нуля. */
    sourceInMs?: number | null
    sourceOutMs?: number | null
  }>,
): TimelineDocument {
  const clips: TimelineClip[] = []
  let offset = 0
  for (const source of sources) {
    const inMs = source.sourceInMs != null ? Math.max(0, Math.round(source.sourceInMs)) : null
    const outMs = source.sourceOutMs != null ? Math.round(source.sourceOutMs) : null
    const windowMs = inMs != null && outMs != null && outMs > inMs ? outMs - inMs : null
    const duration =
      windowMs != null ? windowMs : source.durationMs && source.durationMs > 0 ? Math.round(source.durationMs) : 0
    if (duration <= 0) continue
    const length = Math.max(duration, MIN_CLIP_MS)
    const sourceInMs = windowMs != null && inMs != null ? inMs : 0
    clips.push({
      id: generateId(),
      source_in_ms: sourceInMs,
      source_out_ms: sourceInMs + length,
      timeline_start_ms: offset,
      shot_media_version_id: source.sourceId,
    })
    offset += length
  }

  return {
    schema_version: SCHEMA_VERSION,
    tracks: [
      {
        id: generateId(),
        kind: "video",
        muted: false,
        audio_detached: false,
        hidden: false,
        clips,
      },
    ],
  }
}

/** Одна video-дорожка с полным (или подрезанным) клипом источника (canvas / preview / animatic). */
export function buildSingleVideoDocument(params: {
  sourceId: number
  durationMs: number | null | undefined
  /** Начало в исходнике (мс). По умолчанию 0. */
  sourceInMs?: number
  /** Конец в исходнике (мс). По умолчанию durationMs / fallback. */
  sourceOutMs?: number
}): TimelineDocument {
  const fallbackOut = Math.max(
    params.durationMs && params.durationMs > 0 ? params.durationMs : FALLBACK_CLIP_MS,
    MIN_CLIP_MS,
  )
  const sourceInMs = Math.max(0, Math.round(params.sourceInMs ?? 0))
  const rawOut = params.sourceOutMs != null && params.sourceOutMs > sourceInMs ? params.sourceOutMs : fallbackOut
  const sourceOutMs = Math.max(sourceInMs + MIN_CLIP_MS, Math.round(rawOut))
  return {
    schema_version: SCHEMA_VERSION,
    tracks: [
      {
        id: generateId(),
        kind: "video",
        muted: false,
        audio_detached: false,
        hidden: false,
        shot_media_version_id: params.sourceId,
        clips: [
          {
            id: generateId(),
            source_in_ms: sourceInMs,
            source_out_ms: sourceOutMs,
            timeline_start_ms: 0,
          },
        ],
      },
    ],
  }
}
